import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  ActionButtonsRow,
  Result,
  MathDiv,
  CopyHTMLButton,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  SvgImage,
  ImageDiv,
  CopyImageButton,
  AlertDiv,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { FaRegCopy } from 'react-icons/fa6'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import { MdError } from 'react-icons/md'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function GeotechnicalFlexuralWallStability() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [h, setH] = useState('')
  const [bw, setBw] = useState('')
  const [bs, setBs] = useState('')
  const [t, setT] = useState(0)
  const [ds, setDs] = useState('')
  const [hs, setHs] = useState(0)
  const [gammaSoil, setGammaSoil] = useState(20)
  const [gammaC, setGammaC] = useState(25)
  const [phi, setPhi] = useState(30)
  const [q, setQ] = useState(0)
  const [mu, setMu] = useState(0.55)
  const [sigmaAdm, setSigmaAdm] = useState(150)
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'geotechnical-flexural-wall-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setH(entry.h)
      setBw(entry.bw)
      setBs(entry.bs)
      setT(entry.t)
      setDs(entry.ds)
      setHs(entry.hs)
      setGammaSoil(entry.gammaSoil)
      setGammaC(entry.gammaC)
      setPhi(entry.phi)
      setQ(entry.q)
      setMu(entry.mu)
      setSigmaAdm(entry.sigmaAdm)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (!['q', 'hs', 't'].includes(name) && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.h = validateField('h', h)
    newErrors.bw = validateField('bw', bw)
    newErrors.bs = validateField('bs', bs)
    newErrors.t = validateField('t', t)
    newErrors.ds = validateField('ds', ds)
    newErrors.hs = validateField('hs', hs)
    newErrors.gammaSoil = validateField('gammaSoil', gammaSoil)
    newErrors.gammaC = validateField('gammaC', gammaC)
    newErrors.phi = validateField('phi', phi)
    newErrors.q = validateField('q', q)
    newErrors.mu = validateField('mu', mu)
    newErrors.sigmaAdm = validateField('sigmaAdm', sigmaAdm)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [h, bw, bs, t, ds, hs, gammaSoil, gammaC, phi, q, mu, sigmaAdm, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        h,
        bw,
        bs,
        t,
        ds,
        hs,
        gammaSoil,
        phi,
        gammaC,
        q,
        mu,
        sigmaAdm,
      }

      setData(data)
      const response = await api.post('/geotechnical/flexuralWallStability', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        h,
        bw,
        bs,
        t,
        ds,
        hs,
        gammaSoil,
        gammaC,
        phi,
        q,
        mu,
        sigmaAdm,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      h,
      bw,
      bs,
      t,
      ds,
      hs,
      gammaSoil,
      gammaC,
      phi,
      q,
      mu,
      sigmaAdm,
      result,
      data,
    }

    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Estabilidade Arrimo.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setH(jsonData.h)
        setBw(jsonData.bw)
        setBs(jsonData.bs)
        setT(jsonData.t)
        setDs(jsonData.ds)
        setHs(jsonData.hs)
        setGammaSoil(jsonData.gammaSoil)
        setGammaC(jsonData.gammaC)
        setPhi(jsonData.phi)
        setQ(jsonData.q)
        setMu(jsonData.mu)
        setSigmaAdm(jsonData.sigmaAdm)

        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const svgRef = useRef(null)
  const InputImage = () => {
    return (
      <SvgImage ref={svgRef} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="500" height="500" fill="white" />
        <path
          d="M152.491 409.358V387.637H248.102V68.6455H269.832V387.637H335.022V441.009H312.671V409.358H152.491Z"
          stroke="black"
          strokeWidth="2"
        />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="374" y="393.66">
            {ds != 0 && ds != '' ? ds : 'ds'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="299.885" y="234.219">
            {h != 0 && h != '' ? h : 'h'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="152.06" y="41.1604">
            {q !== '' ? q : 'q'}
          </tspan>
        </text>
        <text
          transform="translate(218.202 441.334)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="14.0283" y="13.1604">
            {bs != 0 && bs != '' ? bs : 'bs'}
          </tspan>
        </text>
        <text
          transform="translate(284.5 143.5)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="11.0498" y="13.1604">
            {bw != 0 && bw != '' ? bw : 'bw'}
          </tspan>
        </text>
        <text
          transform="translate(98 349.5)"
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="end"
        >
          <tspan x="44.7362" y="13.1604">
            {t}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="374" y="425.16">
            {hs}
          </tspan>
        </text>
        <path
          d="M350.993 388.075L348.107 393.075L353.88 393.075L350.993 388.075ZM350.993 410.891L353.88 405.891L348.107 405.891L350.993 410.891ZM350.993 392.575L350.493 392.575L350.493 406.391L350.993 406.391L351.493 406.391L351.493 392.575L350.993 392.575Z"
          fill="black"
        />
        <path
          d="M290.302 68.6455L287.415 73.6455L293.189 73.6455L290.302 68.6455ZM290.302 386.25L293.189 381.25L287.415 381.25L290.302 386.25ZM290.302 73.1455L289.802 73.1455L289.802 381.75L290.302 381.75L290.802 381.75L290.802 73.1455L290.302 73.1455Z"
          fill="black"
        />
        <path
          d="M334.566 460.175L329.566 457.288V463.062L334.566 460.175ZM152.947 460.175L157.947 463.062V457.288L152.947 460.175ZM330.066 460.175V459.675H157.447V460.175V460.675H330.066V460.175Z"
          fill="black"
        />
        <path
          d="M270 165L265 162.113V167.887L270 165ZM249 165L254 167.887V162.113L249 165ZM265.5 165V164.5H253.5V165V165.5H265.5V165Z"
          fill="black"
        />
        <path d="M323 165H270" stroke="black" />
        <path
          d="M350.993 410.891L348.107 415.891L353.88 415.891L350.993 410.891ZM350.993 440.096L353.88 435.096L348.107 435.096L350.993 440.096ZM350.993 415.391L350.493 415.391L350.493 435.596L350.993 435.596L351.493 435.596L351.493 415.391L350.993 415.391Z"
          fill="black"
        />
        <line x1="340.498" y1="410.391" x2="367.878" y2="410.391" stroke="black" />
        <line x1="152.991" y1="412.717" x2="152.991" y2="471.127" stroke="black" />
        <line x1="340.498" y1="440.509" x2="367.878" y2="440.509" stroke="black" />
        <line x1="340.498" y1="387.575" x2="367.878" y2="387.575" stroke="black" />
        <line x1="274.33" y1="68.1455" x2="301.71" y2="68.1455" stroke="black" />
        <line x1="335.066" y1="443.291" x2="335.066" y2="470.67" stroke="black" />
        <line x1="340.498" y1="410.391" x2="367.878" y2="410.391" stroke="black" />
        <path d="M247.491 387.645H152V68.1689H247.491V387.645Z" fill="url(#pattern0_41_2)" stroke="black" />
        <rect x="152.56" y="51" width="94" height="17" stroke="black" />
        <path
          d="M152.56 68.5L155.447 63.5L149.674 63.5L152.56 68.5ZM152.56 51.5L152.06 51.5L152.06 64L152.56 64L153.06 64L153.06 51.5L152.56 51.5Z"
          fill="black"
        />
        <path
          d="M163.06 68.5L165.947 63.5L160.174 63.5L163.06 68.5ZM163.06 51.5L162.56 51.5L162.56 64L163.06 64L163.56 64L163.56 51.5L163.06 51.5Z"
          fill="black"
        />
        <path
          d="M173.56 68.5L176.447 63.5L170.674 63.5L173.56 68.5ZM173.56 51.5L173.06 51.5L173.06 64L173.56 64L174.06 64L174.06 51.5L173.56 51.5Z"
          fill="black"
        />
        <path
          d="M184.06 68.5L186.947 63.5L181.174 63.5L184.06 68.5ZM184.06 51.5L183.56 51.5L183.56 64L184.06 64L184.56 64L184.56 51.5L184.06 51.5Z"
          fill="black"
        />
        <path
          d="M194.56 68.5L197.447 63.5L191.674 63.5L194.56 68.5ZM194.56 51.5L194.06 51.5L194.06 64L194.56 64L195.06 64L195.06 51.5L194.56 51.5Z"
          fill="black"
        />
        <path
          d="M205.06 68.5L207.947 63.5L202.174 63.5L205.06 68.5ZM205.06 51.5L204.56 51.5L204.56 64L205.06 64L205.56 64L205.56 51.5L205.06 51.5Z"
          fill="black"
        />
        <path
          d="M215.56 68.5L218.447 63.5L212.674 63.5L215.56 68.5ZM215.56 51.5L215.06 51.5L215.06 64L215.56 64L216.06 64L216.06 51.5L215.56 51.5Z"
          fill="black"
        />
        <path
          d="M226.06 68.5L228.947 63.5L223.174 63.5L226.06 68.5ZM226.06 51.5L225.56 51.5L225.56 64L226.06 64L226.56 64L226.56 51.5L226.06 51.5Z"
          fill="black"
        />
        <path
          d="M236.56 68.5L239.447 63.5L233.674 63.5L236.56 68.5ZM236.56 51.5L236.06 51.5L236.06 64L236.56 64L237.06 64L237.06 51.5L236.56 51.5Z"
          fill="black"
        />
        <path
          d="M246.56 68.5L249.447 63.5L243.674 63.5L246.56 68.5ZM246.56 51.5L246.06 51.5L246.06 64L246.56 64L247.06 64L247.06 51.5L246.56 51.5Z"
          fill="black"
        />
        <path
          d="M247 370L242 367.113V372.887L247 370ZM152 370L157 372.887V367.113L152 370ZM242.5 370V369.5H156.5V370V370.5H242.5V370Z"
          fill="black"
        />
        <path d="M152 370H114" stroke="black" />
        <line x1="351" y1="399.5" x2="413" y2="399.5" stroke="black" />
        <line x1="351" y1="430" x2="413" y2="430" stroke="black" />
        <defs>
          <pattern
            id="pattern0_41_2"
            patternUnits="userSpaceOnUse"
            patternTransform="matrix(34 0 0 34 151.293 67.4618)"
            preserveAspectRatio="none"
            viewBox="-0.707108 -0.707094 34 34"
            width="1"
            height="1"
          >
            <use xlinkHref="#pattern0_41_2_inner" transform="translate(-34 -34)" />
            <use xlinkHref="#pattern0_41_2_inner" transform="translate(0 -34)" />
            <use xlinkHref="#pattern0_41_2_inner" transform="translate(-34 0)" />
            <g id="pattern0_41_2_inner">
              <line x1="-0.353553" y1="33.6464" x2="33.6464" y2="-0.353553" stroke="#7E7E7E" />
            </g>
          </pattern>
        </defs>
      </SvgImage>
    )
  }

  const handleCopySVG = async () => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 500
      canvas.height = 500
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #16a34a 0%, #15803d 100%)">
        <div className="header-content">
          <BackButton to="/geotecnia">
            <FiArrowLeft /> Voltar para Geotecnia
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/geotecnia">Geotecnia</a>
            <span>/</span>
            <span>Estabilidade de Muro</span>
          </div>
          <h1>Estabilidade de Muro de Arrimo</h1>
          <p className="subtitle">Avaliações de empuxo, escorregamento e tombamento para muros em flexão.</p>
          <div className="badges">
            <Badge>NBR 11682</Badge>
            <Badge>Empuxo ativo/passivo</Badge>
            <Badge>Escorregamento e tombamento</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e9f7ef 0%, #d1fae5 100%)"
        $border="2px solid #34d399"
        $primaryColor="#15803d"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Avalia a estabilidade de muros de arrimo considerando empuxos ativo e passivo, geometrias do muro e da
              sapata, peso específico do solo e sobrecargas aplicadas.
            </p>
            <p>
              O resultado retorna esforços, pontos de aplicação e verificações de segurança para escorregamento e
              tombamento com base no coeficiente de atrito solo-concreto.
            </p>
            <div className="norms">
              <span>NBR 11682:2009</span>
              <span>Empuxo ativo e passivo</span>
              <span>Segurança global</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <ImageDiv>
            <InputImage />
            <CopyImageButton title="Clique para copiar essa imagem" onClick={handleCopySVG}>
              <FaRegCopy />
              Copiar
            </CopyImageButton>
          </ImageDiv>
          <form onSubmit={handleCalculate}>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Altura do talude (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h || ''}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Espessura do muro (cm)</span>
              <ValidatedInput
                name="bw"
                type="number"
                value={bw || ''}
                onChange={(event) => setBw(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Largura da sapata (cm)</span>
              <ValidatedInput
                name="bs"
                type="number"
                value={bs || ''}
                onChange={(event) => setBs(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Talão da sapata (cm)</span>
              <ValidatedInput
                name="t"
                type="number"
                value={t}
                onChange={(event) => setT(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura da sapata (cm)</span>
              <ValidatedInput
                name="ds"
                type="number"
                value={ds || ''}
                onChange={(event) => setDs(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura do dente (cm)</span>
              <ValidatedInput
                name="hs"
                type="number"
                value={hs}
                onChange={(event) => setHs(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <InputDiv>
              <span>Tensão admissível do solo (kPa)</span>
              <ValidatedInput
                name="sigmaAdm"
                type="number"
                value={sigmaAdm || ''}
                onChange={(event) => setSigmaAdm(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>
                &gamma;<sub>solo</sub> (kN/m³)
              </span>
              <ValidatedInput
                name="gammaSoil"
                type="number"
                value={gammaSoil || ''}
                onChange={(event) => setGammaSoil(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>
                &gamma;<sub>concreto</sub> (kN/m³)
              </span>
              <ValidatedInput
                name="gammaConcrete"
                type="number"
                value={gammaC || ''}
                onChange={(event) => setGammaC(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Ângulo de talude natural (º)</span>
              <ValidatedInput
                name="phi"
                type="number"
                value={phi || ''}
                onChange={(event) => setPhi(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Sobrecarga no topo do talude (kN/m²)</span>
              <ValidatedInput
                name="q"
                type="number"
                value={q}
                onChange={(event) => setQ(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de atrito</span>
              <ValidatedInput
                name="mu"
                type="number"
                value={mu || ''}
                onChange={(event) => setMu(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Verificação de Estabilidade</h3>
              <MathDiv>
                <h4>Escorregamento</h4>
                <AlertDiv>
                  <BlockMath
                    math={`\\epsilon_1 = \\dfrac{\\left( ${result.totalVertical.toFixed(2)} \\right) \\cdot ${data.mu} }{${result.totalHorizontal.toFixed(2)}} = ${result.epsilon1.toFixed(2)}`}
                  />
                  {result.epsilon1 < 1.5 && (
                    <MdError
                      data-tooltip-id="stability-tooltip"
                      data-tooltip-content="Fator de segurança < 1,5. Ajuste geometria, atrito ou pesos para evitar escorregamento."
                    />
                  )}
                </AlertDiv>
                <h4>Tombamento</h4>
                <AlertDiv>
                  <BlockMath
                    math={`\\epsilon_2 = \\dfrac{M_i}{M_e} = \\dfrac{${result.wallM.toFixed(2)}}{${result.eM.toFixed(2)}} = ${result.epsilon2.toFixed(2)}`}
                  />
                  {result.epsilon2 < 1.5 && (
                    <MdError
                      data-tooltip-id="stability-tooltip"
                      data-tooltip-content="Fator de segurança < 1,5. Aumente peso, braço de alavanca ou base para evitar tombamento."
                    />
                  )}
                </AlertDiv>
              </MathDiv>
              <h3>Tensões no solo</h3>
              <MathDiv>
                <h4>Tensão média</h4>
                <BlockMath math={`\\sigma_{méd} = ${result.sigmam.toFixed(2)} \\text{ kPa}`} />
                <BlockMath math={`\\sigma_{máx} = ${result.sigmamax1.toFixed(2)} \\text{ kPa}`} />
                <BlockMath math={`\\sigma_{mín} = ${result.sigmamin.toFixed(2)} \\text{ kPa}`} />
                {result.sigmamin < 0 ? (
                  <>
                    <BlockMath
                      math={`\\sigma_{mín} < 0 \\therefore \\sigma_{máx} = ${result.sigmamax.toFixed(2)} \\text{ kPa}`}
                    />
                  </>
                ) : (
                  <>
                    <BlockMath math={`\\sigma_{máx} = ${result.sigmamax.toFixed(2)} \\text{ kPa}`} />
                  </>
                )}
                {result.sigmaIsOver ? (
                  <>
                    <AlertDiv>
                      <BlockMath math={`\\sigma_{máx} > \\sigma_{adm}`} />
                      <MdError />
                    </AlertDiv>
                    <p>Tensão no solo acima do limite admissível.</p>
                  </>
                ) : (
                  <>
                    <BlockMath math={`\\sigma_{máx} \\leq \\sigma_{adm}`} />
                  </>
                )}
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Cálculo do Empuxo</h3>
              <MathDiv>
                <h4>Empuxo ativo</h4>
                <BlockMath
                  math={`h_0 = \\dfrac{q}{\\gamma_{solo}}  = \\dfrac{${data.q} }{${data.gammaSoil}} = ${result.thrust.h0} \\text{ m}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`K = tan{\\left(45º - \\dfrac{\\phi}{2} \\right)}^2 = tan{\\left(45º - \\dfrac{${data.phi}º}{2} \\right)}^2 = ${result.thrust.k.toFixed(3)}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`K = tan{\\left(45º - \\dfrac{\\phi}{2} \\right)}^2`} />
                  <BlockMath
                    math={`K = tan{\\left(45º - \\dfrac{${data.phi}º}{2} \\right)}^2 = ${result.thrust.k.toFixed(3)}`}
                  />
                </MobileOnlyDiv>
                <BlockMath
                  math={`H_{total} = h + h_0 = ${result.thrust.h0} + ${data.h / 100} = ${result.thrust.totalH} \\text{ m}`}
                />
                <BlockMath
                  math={`E = \\dfrac{1}{2} \\cdot K \\cdot \\gamma_{solo} \\cdot \\left({H_{total}}^2 - {h_0}^2 \\right)`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\boxed{E = \\dfrac{1}{2} \\cdot ${result.thrust.k.toFixed(3)} \\cdot ${data.gammaSoil} \\cdot \\left({${result.thrust.totalH}}^2 - {${result.thrust.h0}}^2 \\right) = ${result.thrust.e.toFixed(1)} \\text{ kN/m}}`}
                  />
                  <BlockMath
                    math={`y = \\dfrac{h}{3} \\cdot \\dfrac{2 \\cdot h_0 + H_{total}}{h_0 + H_{total}} = \\dfrac{${data.h}}{3} \\cdot \\dfrac{2 \\cdot ${result.thrust.h0} + ${result.thrust.totalH}}{${result.thrust.h0} + ${result.thrust.totalH}} = ${result.thrust.y.toFixed(2)} \\text{ m}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`E = \\dfrac{1}{2} \\cdot ${result.thrust.k.toFixed(3)} \\cdot ${data.gammaSoil} \\cdot \\left({${result.thrust.totalH}}^2 - {${result.thrust.h0}}^2 \\right)`}
                  />
                  <BlockMath math={`\\boxed{E = ${result.thrust.e.toFixed(1)} \\text{ kN/m}}`} />
                  <BlockMath math={`y = \\dfrac{h}{3} \\cdot \\dfrac{2 \\cdot h_0 + H_{total}}{h_0 + H_{total}}`} />
                  <BlockMath
                    math={`y = \\dfrac{${data.h}}{3} \\cdot \\dfrac{2 \\cdot ${result.thrust.h0} + ${result.thrust.totalH}}{${result.thrust.h0} + ${result.thrust.totalH}} = ${result.thrust.y.toFixed(2)} \\text{ m}`}
                  />
                </MobileOnlyDiv>
                <h4>Empuxo passivo</h4>
                <BlockMath
                  math={`K_0 = \\dfrac{1}{K} = \\dfrac{1}{${result.thrust.k.toFixed(3)}} = ${result.thrust.k0.toFixed(3)}`}
                />
                <BlockMath
                  math={`E_0 = \\dfrac{1}{2} \\cdot K_0 \\cdot \\gamma_{solo} \\cdot \\left( d_s + h_s \\right)^2`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\boxed{E_0 = \\dfrac{1}{2} \\cdot ${result.thrust.k0.toFixed(3)} \\cdot ${data.gammaSoil} \\cdot \\left( ${data.ds / 100} + ${data.hs / 100} \\right)^2 = ${result.passiveThrust.toFixed(1)} \\text{ kN/m}}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`E_0 = \\dfrac{1}{2} \\cdot ${result.thrust.k0.toFixed(3)} \\cdot ${data.gammaSoil} \\cdot \\left( ${data.ds / 100} + ${data.hs / 100} \\right)^2`}
                  />
                  <BlockMath math={`\\boxed{E_0 = ${result.passiveThrust.toFixed(1)} \\text{ kN/m}}`} />
                </MobileOnlyDiv>
                <BlockMath
                  math={`z_0 = h_s + d_s = ${data.hs / 100} + ${data.ds / 100} = ${result.z0.toFixed(2)} \\text{ m}`}
                />
                <BlockMath
                  math={`y_0 = \\dfrac{z0}{3} = \\dfrac{${result.z0.toFixed(2)}}{3} = ${result.y0.toFixed(2)} \\text{ m}`}
                />
              </MathDiv>
              <h3>Cargas verticais</h3>
              <MathDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`G_{muro} = b_w \\cdot h \\cdot \\gamma_{concreto} = ${data.bw / 100} \\cdot ${data.h / 100} \\cdot ${data.gammaC} = ${result.wallWeight.toFixed(1)} \\text{ kN/m}`}
                  />
                  <BlockMath
                    math={`G_{base} = b_s \\cdot d_s \\cdot \\gamma_{concreto} = ${data.bs / 100} \\cdot ${data.ds / 100} \\cdot ${data.gammaC} = ${result.baseWeight.toFixed(1)} \\text{ kN/m}`}
                  />
                  <BlockMath
                    math={`G_{sobre,talão} = t \\cdot h \\cdot \\gamma_{solo} = ${data.t / 100} \\cdot ${data.h / 100} \\cdot ${data.gammaSoil} = ${result.soilOverHeel.toFixed(1)} \\text{ kN/m}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`G_{muro} = b_w \\cdot h \\cdot \\gamma_{concreto}`} />
                  <BlockMath
                    math={`G_{muro} = ${data.bw / 100} \\cdot ${data.h / 100} \\cdot ${data.gammaC} = ${result.wallWeight.toFixed(1)} \\text{ kN/m}`}
                  />
                  <BlockMath math={`G_{base} = b_s \\cdot d_s \\cdot \\gamma_{concreto}`} />
                  <BlockMath
                    math={`G_{base} = ${data.bs / 100} \\cdot ${data.ds / 100} \\cdot ${data.gammaC} = ${result.baseWeight.toFixed(1)} \\text{ kN/m}`}
                  />
                  <BlockMath math={`G_{sobre,talão} = t \\cdot h \\cdot \\gamma_{solo}`} />
                  <BlockMath
                    math={`G_{sobre,talão} = ${data.t / 100} \\cdot ${data.h / 100} \\cdot ${data.gammaSoil} = ${result.soilOverHeel.toFixed(1)} \\text{ kN/m}`}
                  />
                </MobileOnlyDiv>
              </MathDiv>
              <h3>Braços de alavanca</h3>
              <MathDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`x_{muro} = b_s - t - \\dfrac{b_w}{2}`} />
                  <BlockMath
                    math={`x_{muro}  = ${data.bs / 100} - ${data.t / 100} - \\dfrac{${data.bw / 100}}{2} = ${result.xWall.toFixed(2)} \\text{ m}`}
                  />
                  <BlockMath
                    math={`x_{base} = \\dfrac{b_s}{2} = \\dfrac{${data.bs / 100}}{2} = ${result.xBase.toFixed(2)} \\text{ m}`}
                  />
                  <BlockMath math={`x_{sobre,talão} = b_s - \\dfrac{t}{2}`} />
                  <BlockMath
                    math={`x_{sobre,talão} = ${data.bs / 100} - \\dfrac{${data.t / 100}}{2} = ${result.xHeel.toFixed(2)} \\text{ m}`}
                  />
                  <BlockMath math={`y_{empuxo} = y + d_s`} />
                  <BlockMath
                    math={`y_{empuxo} = ${result.thrust.y.toFixed(2)} + ${data.ds / 100} = ${result.ye.toFixed(2)} \\text{ m}`}
                  />
                </MobileOnlyDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`x_{muro} = b_s - t - \\dfrac{b_w}{2} = ${data.bs / 100} - ${data.t / 100} - \\dfrac{${data.bw / 100}}{2} = ${result.xWall.toFixed(2)} \\text{ m}`}
                  />
                  <BlockMath
                    math={`x_{base} = \\dfrac{b_s}{2} = \\dfrac{${data.bs / 100}}{2} = ${result.xBase.toFixed(2)} \\text{ m}`}
                  />
                  <BlockMath
                    math={`x_{sobre,talão} = b_s - \\dfrac{t}{2} = ${data.bs / 100} - \\dfrac{${data.t / 100}}{2} = ${result.xHeel.toFixed(2)} \\text{ m}`}
                  />
                  <BlockMath
                    math={`y_{empuxo} = y + d_s = ${result.thrust.y.toFixed(2)} + ${data.ds / 100} = ${result.ye.toFixed(2)} \\text{ m}`}
                  />
                </DesktopOnlyDiv>
              </MathDiv>
              <h3>Momentos</h3>
              <MathDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`M_i = G_{muro} \\cdot x_{muro} + G_{base} \\cdot x_{base} + G_{sobre,talão} \\cdot x_{sobre,talão}`}
                  />
                  <BlockMath
                    math={`\\boxed{M_i = ${result.wallWeight.toFixed(1)} \\cdot ${result.xWall.toFixed(2)} + ${result.baseWeight.toFixed(1)} \\cdot ${result.xBase.toFixed(2)} + ${result.soilOverHeel.toFixed(1)} \\cdot ${result.xHeel.toFixed(2)} = ${result.wallM.toFixed(1)} \\text{ kN.m/m}}`}
                  />
                  <BlockMath math={`M_e = E \\cdot y_{empuxo} + E_0 \\cdot \\left( z_0 - y_0 - d_s \\right)`} />
                  <BlockMath
                    math={`\\boxed{M_e = ${result.thrust.e.toFixed(2)} \\cdot ${result.ye.toFixed(2)} + ${result.passiveThrust.toFixed(1)} \\cdot \\left( ${result.z0.toFixed(2)} - ${result.y0.toFixed(2)} - ${data.ds / 100} \\right) = ${result.eM.toFixed(1)} \\text{ kN.m/m}}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`M_i = G_{muro} \\cdot x_{muro} + G_{base} \\cdot x_{base} \\cdots`} />
                  <BlockMath math={`\\cdots + G_{sobre,talão} \\cdot x_{sobre,talão}`} />
                  <BlockMath
                    math={`M_i = ${result.wallWeight.toFixed(1)} \\cdot ${result.xWall.toFixed(2)} + ${result.baseWeight.toFixed(1)} \\cdot ${result.xBase.toFixed(2)} \\cdots`}
                  />
                  <BlockMath
                    math={`\\boxed{\\cdots + ${result.soilOverHeel.toFixed(1)} \\cdot ${result.xHeel.toFixed(2)} = ${result.wallM.toFixed(1)} \\text{ kN.m/m}}`}
                  />
                  <BlockMath math={`M_e = E \\cdot y_{empuxo} + E_0 \\cdot \\left( z_0 - y_0 - d_s \\right)`} />
                  <BlockMath math={`M_e = ${result.thrust.e.toFixed(2)} \\cdot ${result.ye.toFixed(2)} + \\cdots`} />
                  <BlockMath
                    math={`\\cdots + ${result.passiveThrust.toFixed(1)} \\cdot \\left( ${result.z0.toFixed(2)} - ${result.y0.toFixed(2)} - ${data.ds / 100} \\right)`}
                  />
                  <BlockMath math={`\\boxed{M_e = ${result.eM.toFixed(1)} \\text{ kN.m/m}}`} />
                </MobileOnlyDiv>
              </MathDiv>
              <h3>Verificação de Estabilidade</h3>
              <MathDiv>
                <h4>Escorregamento</h4>
                <BlockMath
                  math={`\\epsilon_1 = \\dfrac{\\left( G_{muro} + G_{base} + G_{sobre,talão} \\right) \\cdot \\mu}{E - E_0}`}
                />
              </MathDiv>
              <MathDiv>
                <AlertDiv>
                  <BlockMath
                    math={`\\boxed{\\epsilon_1 = \\dfrac{\\left( ${result.totalVertical.toFixed(2)} \\right) \\cdot ${data.mu} }{${result.totalHorizontal.toFixed(2)}} = ${result.epsilon1.toFixed(2)}}`}
                  />
                  {result.epsilon1 < 1.5 && (
                    <MdError
                      data-tooltip-id="stability-tooltip"
                      data-tooltip-content="Fator de segurança < 1,5. Ajuste geometria, atrito ou pesos para evitar escorregamento."
                    />
                  )}
                </AlertDiv>
              </MathDiv>
              <MathDiv>
                <h4>Tombamento</h4>
                <AlertDiv>
                  <BlockMath
                    math={`\\boxed{\\epsilon_2 = \\dfrac{M_i}{M_e} = \\dfrac{${result.wallM.toFixed(2)}}{${result.eM_t.toFixed(2)}} = ${result.epsilon2.toFixed(2)}}`}
                  />
                  {result.epsilon2 < 1.5 && (
                    <MdError
                      data-tooltip-id="stability-tooltip"
                      data-tooltip-content="Fator de segurança < 1,5. Aumente peso, braço de alavanca ou base para evitar tombamento."
                    />
                  )}
                </AlertDiv>
              </MathDiv>
              <h3>Tensões no solo</h3>
              <MathDiv>
                <h4>Posição do centro de pressão</h4>
                <BlockMath
                  math={`u = \\dfrac{M}{N}= \\dfrac{${result.eM.toFixed(2)}}{${result.totalVertical}} = ${result.u.toFixed(2)} \\text{ m}`}
                />
                <BlockMath
                  math={`e = \\dfrac{b_s}{2} - u = \\dfrac{${data.bs / 100}}{2} - ${result.u.toFixed(2)} = ${result.e.toFixed(2)} \\text{ m}`}
                />
                <h4>Tensão média</h4>
                <BlockMath
                  math={`\\sigma_{méd} = \\dfrac{N}{b_s} = \\dfrac{${result.totalVertical}}{${data.bs / 100}} = ${result.sigmam.toFixed(2)} \\text{ kPa}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{máx} = \\sigma_{méd} \\cdot \\left( 1 + 6 \\cdot \\dfrac{e}{b_s} \\right) = ${result.sigmam.toFixed(2)} \\cdot \\left( 1 + 6 \\cdot \\dfrac{${result.e.toFixed(2)}}{${data.bs / 100}} \\right) = ${result.sigmamax1.toFixed(2)} \\text{ kPa}`}
                  />
                  <BlockMath
                    math={`\\sigma_{mín} = \\sigma_{méd} \\cdot \\left( 1 - 6 \\cdot \\dfrac{e}{b_s} \\right) = ${result.sigmam.toFixed(2)} \\cdot \\left( 1 - 6 \\cdot \\dfrac{${result.e.toFixed(2)}}{${data.bs / 100}} \\right) = ${result.sigmamin.toFixed(2)} \\text{ kPa}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`\\sigma_{máx} = \\sigma_{méd} \\cdot \\left( 1 + 6 \\cdot \\dfrac{e}{b_s} \\right) `}
                  />
                  <BlockMath
                    math={`\\sigma_{máx} = ${result.sigmam.toFixed(2)} \\cdot \\left( 1 + 6 \\cdot \\dfrac{${result.e.toFixed(2)}}{${data.bs / 100}} \\right)`}
                  />
                  <BlockMath math={`\\sigma_{máx} = ${result.sigmamax1.toFixed(2)} \\text{ kPa}`} />
                  <BlockMath
                    math={`\\sigma_{mín} = \\sigma_{méd} \\cdot \\left( 1 - 6 \\cdot \\dfrac{e}{b_s} \\right)`}
                  />
                  <BlockMath
                    math={`\\sigma_{mín} = ${result.sigmam.toFixed(2)} \\cdot \\left( 1 - 6 \\cdot \\dfrac{${result.e.toFixed(2)}}{${data.bs / 100}} \\right)`}
                  />
                  <BlockMath math={`\\sigma_{mín} = ${result.sigmamin.toFixed(2)} \\text{ kPa}`} />
                </MobileOnlyDiv>
                {result.sigmamin < 0 ? (
                  <>
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`\\boxed{\\sigma_{mín} < 0 \\therefore \\sigma_{máx} = \\dfrac{2 \\cdot N}{3 \\cdot u} = \\dfrac{2 \\cdot ${result.totalVertical}}{3 \\cdot ${result.u.toFixed(3)}} = ${result.sigmamax.toFixed(2)} \\text{ kPa}}`}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`\\sigma_{mín} < 0 `} />
                      <BlockMath
                        math={`\\sigma_{máx} = \\dfrac{2 \\cdot N}{3 \\cdot u} = \\dfrac{2 \\cdot ${result.totalVertical}}{3 \\cdot ${result.u.toFixed(3)}} `}
                      />
                      <BlockMath math={`\\boxed{\\sigma_{máx} = ${result.sigmamax.toFixed(2)} \\text{ kPa}}`} />
                    </MobileOnlyDiv>
                  </>
                ) : (
                  <>
                    <BlockMath math={`\\boxed{\\sigma_{máx} = ${result.sigmamax.toFixed(2)} \\text{ kPa}}`} />
                  </>
                )}
                {result.sigmaIsOver ? (
                  <>
                    <AlertDiv>
                      <BlockMath math={`\\sigma_{máx} > \\sigma_{adm}`} />
                      <MdError />
                    </AlertDiv>
                    <p>Tensão no solo acima do limite admissível.</p>
                  </>
                ) : (
                  <>
                    <BlockMath math={`\\sigma_{máx} \\leq \\sigma_{adm}`} />
                  </>
                )}
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
      <Tooltip id="stability-tooltip" style={{ whiteSpace: 'pre-line' }} place="top" />
    </PageContainer>
  )
}
